import { cn } from "@gettactic/helpers";
import React from "react";

export interface PanelShellProps extends React.PropsWithChildren {
  title: React.ReactNode;
  icon?: React.FC<{ size?: number | string; className?: string }>;
  /** Right-aligned header actions (IconButtons). */
  actions?: React.ReactNode;
  className?: string;
}

/**
 * The single panel container for sidebar panels: 40px header with icon,
 * title and actions, scrollable body. Replaces the former
 * EditorPanel/SidebarPanel pair.
 */
export const PanelShell: React.FC<PanelShellProps> = ({
  title,
  icon,
  actions,
  className,
  children
}) => {
  return (
    <div
      className={cn(
        "flex h-full w-full flex-col bg-background-primary",
        className
      )}
    >
      <div className="flex h-10 flex-none items-center justify-between border-neutral-200 border-b px-3">
        <div className="flex min-w-0 items-center gap-2">
          {icon
            ? React.createElement(icon, {
                size: 15,
                className: "flex-none text-neutral-500"
              })
            : undefined}
          <span className="truncate font-semibold text-[13px] text-neutral-900">
            {title}
          </span>
        </div>
        {actions ? (
          <div className="flex flex-none items-center gap-0.5">{actions}</div>
        ) : undefined}
      </div>
      <div className="min-h-0 flex-1 overflow-auto">{children}</div>
    </div>
  );
};

/**
 * A titled section inside a panel body: consistent padding and an
 * uppercase section label, separated by a hairline.
 */
export const PanelSection: React.FC<
  React.PropsWithChildren<{ label?: React.ReactNode; className?: string }>
> = ({ label, className, children }) => {
  return (
    <div className={cn("border-neutral-200 border-b px-3.5 py-3", className)}>
      {label ? <EditorSectionLabel>{label}</EditorSectionLabel> : undefined}
      {children}
    </div>
  );
};

export const EditorSectionLabel: React.FC<React.PropsWithChildren> = ({
  children
}) => (
  <p className="mb-2.5 font-semibold text-[11px] text-neutral-400 uppercase tracking-wider">
    {children}
  </p>
);
